// The browser-side subscriber composition root — BL-1517, ADR 046 step 5.
//
// CLIENT-ONLY, and deliberately NOT re-exported from `./index.ts`: it reaches
// `./gateway-socket`, which imports `socket.io-client`, and the realtime barrel
// is imported by server code. Per `sharing.md` §3's root-barrel hygiene it is
// reached by subpath — `@auctogon/core/realtime/create-subscriber`.
//
// It lives here rather than in each app for the same reason `create-publisher.ts`
// does: admin-web and bidder-web need the identical composition, and
// `sharing.md` §1 caps a per-app wrapper at a few lines.
//
// THIS REPLACED `select-subscriber.ts`, which replaced `gateway-observation.ts`
// before it — the third and last step of the same retirement:
//
//   gateway-observation  the gateway WATCHED Supabase, and the two were compared
//   select-subscriber    the gateway DELIVERED, Supabase stayed as the fallback
//   create-subscriber    Supabase is gone; there is nothing to select between
//
// Each rename tracked a real change in what the module decides. Keeping the name
// `selectRealtimeSubscriber` for a function with one transport would describe a
// choice the code can no longer make.

import 'client-only';

import { createDeferredSubscriber } from '../../transport/realtime/deferred-subscriber';
import { REALTIME_GATEWAY_MISSING_NOTICE } from '../../isomorphic/realtime/gateway-configuration-notice';
import { createPresenceTokenFetcher } from './presence-token-fetcher';
import type { PresenceTokenDto } from '../../schemas/responses/presence-token';
import type { RealtimeConnectionEvent, RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';

/** Options for {@link createRealtimeSubscriber}. */
export interface CreateRealtimeSubscriberOptions {
    /**
     * Gateway origin, from `NEXT_PUBLIC_REALTIME_GATEWAY_URL`.
     *
     * Passed IN rather than read from `process.env` here on purpose: `NEXT_PUBLIC_*`
     * is inlined by Next at build time at the literal `process.env.X` site, so
     * keeping that site in app code makes the substitution obvious instead of
     * depending on how a workspace package happens to be transpiled.
     */
    gatewayUrl?: string;
    /**
     * Mint a presence join token (BL-410). Forwarded to the gateway subscriber,
     * which sends it on a presence room join so a DB-free gateway can assert who
     * the connection belongs to.
     */
    getJoinToken?: (topic: string) => Promise<string | null>;
}

/**
 * A subscriber for a tab with no gateway configured.
 *
 * Accepts subscriptions, delivers nothing, and **never throws**. There is no
 * second transport to fall back to any more, so the alternative would be
 * throwing at provider construction — which takes down every page in an
 * environment whose gateway URL happens to be blank. This is the same posture
 * `createDeferredSubscriber` already takes when the dynamic import fails
 * (BL-1495 AC 5): a bus outage must not break rendering. Catch-up still serves
 * current state on mount; the page simply stops updating live.
 *
 * IT SAYS SO, ONCE (BUG-1454). Being inert is the right behavior; being inert
 * in SILENCE was not. Post-BL-1517 there is no second transport, so "inert"
 * means realtime is entirely dead — and every observable signal still reported
 * a healthy app: pages rendered, catch-up returned correct data, no request
 * failed, no console line, no page error. What was once graceful degradation
 * became an undiagnosable outage that cost hours. The publisher has warned on
 * an unset `REDIS_HOST` since it was written (`createNotConfiguredPublisher` in
 * `create-publisher.ts`); the subscriber not doing the same was an asymmetry,
 * not a decision.
 *
 * @returns A RealtimeSubscriber that is inert but safe to call and dispose.
 * @sideEffect Writes one `console.warn` on the first `subscribe()`.
 */
function createInertSubscriber(): RealtimeSubscriber {
    // Both disposers are real functions, not `undefined`. Consumers call them in
    // a `useEffect` cleanup that runs on every unmount — twice under React
    // strict mode — so returning nothing here is a TypeError at teardown on
    // every provider in the app.
    const noopDispose: Unsubscribe = () => {};

    // Per SUBSCRIBER, not per subscription and not per module. The subscriber is
    // memoized for the tab's lifetime by `createRealtimeSubscriberSingleton`, so
    // this is one line per tab in production — while a per-subscription line
    // would print once per topic (the clerk console alone subscribes to
    // several), and a wall of identical warnings gets read as noise and filtered
    // out, which is the same silence with extra steps. Module-level state would
    // additionally leak between test cases in one file.
    let announced = false;
    // BL-2342 — there is no socket to close, but offline mode still has to
    // stop every catch-up, and the stores read the flag off the subscriber.
    let suspended = false;
    // Held only so a resume can report 'reconnected', as the socket adapter
    // does, and the providers catch up the topics they held while suspended.
    // It is the documented exception to "never before a real connect"
    // (`RealtimeSubscriber.setSuspended`): there is no connection to prove, so
    // a resume over a dead network briefly reads as alive until the connection
    // monitor's next probe fails again (about 2s).
    const connectionHandlers = new Set<(event: RealtimeConnectionEvent) => void>();

    // ON FIRST SUBSCRIBE rather than at construction: the subscriber is built in
    // the provider on every page, including ones that subscribe to nothing at
    // all. Warning there would put the line in front of readers it does not
    // apply to — the fastest way to teach everyone to ignore it.
    return {
        subscribe: () => {
            if (!announced) {
                announced = true;
                console.warn(REALTIME_GATEWAY_MISSING_NOTICE);
            }
            return noopDispose;
        },
        onConnectionChange: (handler) => {
            connectionHandlers.add(handler);
            return () => {
                connectionHandlers.delete(handler);
            };
        },
        setSuspended: (next: boolean) => {
            const resumed = suspended && !next;
            suspended = next;
            if (resumed) for (const h of [...connectionHandlers]) h('reconnected');
        },
        isSuspended: () => suspended,
    };
}

/**
 * Build the per-tab subscriber for snapshot + ledger + presence topics.
 *
 * A URL yields the socket.io gateway; no URL yields an inert subscriber. Callers
 * memoize the result for the tab's lifetime so one socket serves every topic.
 *
 * @param options - Gateway URL and the presence join-token provider.
 * @returns The gateway-backed subscriber, or an inert one.
 *
 * @sideEffect Opens a long-lived WebSocket to the gateway when a URL is given.
 */
export function createRealtimeSubscriber({
    gatewayUrl,
    getJoinToken,
}: CreateRealtimeSubscriberOptions = {}): RealtimeSubscriber {
    const url = gatewayUrl?.trim();
    if (!url) return createInertSubscriber();

    // DYNAMIC import, deliberately. A static one pulled the socket.io client
    // (~30 KB gzipped, measured) into bidder-web's `[tenant]` layout chunk on
    // every page in every environment — including the ones where this URL is
    // empty and no socket is ever opened. Deferring it means the transport
    // library is fetched only where the gateway is actually serving.
    //
    // `createDeferredSubscriber` swallows a failed load, which is the BL-1495
    // AC 5 degrade: catch-up still renders current state on mount and the page
    // stops updating live, rather than erroring.
    return createDeferredSubscriber(async () => {
        const { createGatewaySubscriber } = await import('../../transport/realtime/gateway-socket');
        return createGatewaySubscriber({ url, getJoinToken });
    });
}

let cachedSubscriber: RealtimeSubscriber | null = null;

/** Options for {@link createRealtimeSubscriberSingleton}. */
export interface RealtimeSubscriberSingletonOptions {
    /**
     * Gateway origin. MUST be passed from a literal `process.env.NEXT_PUBLIC_*`
     * site in app code — see {@link CreateRealtimeSubscriberOptions.gatewayUrl}
     * for why the read cannot move in here.
     */
    gatewayUrl?: string;
    /**
     * Mint a presence token for one auction through THIS app's typed API client
     * (BL-410). **This is the one genuine per-app difference in the realtime
     * adapter layer** (BL-1586 AC 3): admin-web reaches
     * `@/lib/api-client/typed-client` and bidder-web reaches `@/lib/api-client`,
     * two different generated clients over two different route registries. It is
     * a named parameter rather than two parallel files precisely so the
     * divergence is one readable line at each call site instead of a whole
     * duplicated module (ADR 049 — Shared Fan-Out Decision 2's declare-don't-omit
     * discipline, applied to a difference that is real).
     *
     * Returns `null` when the caller is not entitled to presence on that auction
     * (e.g. an unapproved bidder gets a 403) — an ordinary outcome, not an error.
     *
     * Optional: an app that joins no presence room passes none, and its subscriber
     * requests no join token. admin-web is that app since BL-1556 removed its
     * only presence subscription and BL-1833 PR 3a deleted its mint route.
     */
    mintPresenceToken?: (auctionId: string) => Promise<PresenceTokenDto | null>;
}

/**
 * The per-tab memoized `RealtimeSubscriber`.
 *
 * Memoized so React strict-mode double-mounts and repeated invocations share one
 * underlying subscriber — and therefore ONE socket. BL-1586 moved this out of
 * both apps, which carried the same body differing only in the import path of
 * the client that mints the presence token; that difference is now the
 * {@link RealtimeSubscriberSingletonOptions.mintPresenceToken} parameter.
 *
 * @param options - Gateway URL and, for an app that joins presence rooms, its presence-token minter.
 * @returns The memoized subscriber for this tab.
 * @sideEffect First call opens a long-lived WebSocket when a gateway URL is
 *   given; inert (and safe) when it is not.
 */
export function createRealtimeSubscriberSingleton({
    gatewayUrl,
    mintPresenceToken,
}: RealtimeSubscriberSingletonOptions): RealtimeSubscriber {
    return (cachedSubscriber ??= createRealtimeSubscriber({
        gatewayUrl,
        // A provider rather than a token: tokens are short-lived and rejoin
        // fires on every reconnect.
        getJoinToken:
            mintPresenceToken == null ? undefined : createPresenceTokenFetcher({ mint: mintPresenceToken }),
    }));
}
